/**
 * 数据选择器工厂组件
 *
 * 根据选择器类型动态渲染对应的数据选择器组件。
 * 支持通过 type 属性指定选择器类型，或通过 contentBlocks 自动推断。
 *
 * 使用方式：
 * <DataSelectorFactory type="operationFlight" value={data} onChange={setData} />
 * 或
 * <DataSelectorFactory contentBlocks={["operationFlightList"]} value={data} onChange={setData} />
 */

import React, { Suspense, useMemo } from "react";
import { Empty, Spin, Typography, Button } from "antd";
import { DatabaseOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { DATA_SELECTOR_REGISTRY, inferSelectorFromContentBlocks } from "./index";

const { Text } = Typography;

/**
 * 数据选择器统一 Props 接口
 * @typedef {Object} DataSelectorProps
 * @property {any[]} value - 已选数据数组
 * @property {function} onChange - 数据变更回调 (newData: any[]) => void
 * @property {any[]} [presetData] - 预设数据（初始化时使用）
 * @property {"single"|"multiple"} [mode="multiple"] - 选择模式
 * @property {boolean} [disabled=false] - 是否禁用
 * @property {number} [maxSelection] - 最大选择数量
 * @property {object} [filterParams] - 额外筛选参数
 * @property {boolean} [allowSkip=true] - 是否允许跳过（不选数据直接继续）
 * @property {function} [onSkip] - 跳过回调
 */

/**
 * 数据选择器工厂组件
 */
const DataSelectorFactory = ({ type, contentBlocks, value = [], onChange, presetData, mode = "multiple", disabled = false, maxSelection, filterParams, allowSkip = true, onSkip, style }) => {
    // 推断选择器类型
    const selectorType = useMemo(() => {
        if (type) return type;
        if (contentBlocks) return inferSelectorFromContentBlocks(contentBlocks);
        return null;
    }, [type, contentBlocks]);

    // 获取选择器配置
    const selectorConfig = useMemo(() => {
        if (!selectorType) return null;
        return DATA_SELECTOR_REGISTRY[selectorType];
    }, [selectorType]);

    // 如果没有匹配的选择器类型，显示空状态
    if (!selectorConfig) {
        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    padding: 24,
                    ...style,
                }}
            >
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<Text type="secondary">此模板无需选择业务数据</Text>} />
                {allowSkip && onSkip && (
                    <Button type="primary" icon={<ArrowRightOutlined />} onClick={onSkip} style={{ marginTop: 16 }}>
                        直接打开编辑器
                    </Button>
                )}
            </div>
        );
    }

    // 渲染选择器组件
    const SelectorComponent = selectorConfig.component;

    return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", ...style }}>
            {/* 选择器标题 */}
            <div
                style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid #f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <DatabaseOutlined style={{ color: "#1890ff" }} />
                    <Text strong>{selectorConfig.label}</Text>
                    {value.length > 0 && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            已选 {value.length} 条
                        </Text>
                    )}
                </div>
            </div>

            {/* 选择器主体 */}
            <div style={{ flex: 1, overflow: "hidden" }}>
                <Suspense
                    fallback={
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                            <Spin tip="加载选择器..." />
                        </div>
                    }
                >
                    <SelectorComponent value={value} onChange={onChange} presetData={presetData} mode={mode} disabled={disabled} maxSelection={maxSelection} filterParams={filterParams} />
                </Suspense>
            </div>
        </div>
    );
};

export default DataSelectorFactory;
